/**
 * SITE THEME — the runtime-editable color + background-pattern scheme for the marketing website.
 *
 * The palette + background pattern are stored under the live-tuning key `siteTheme` (see tuningClient.ts),
 * so an admin can change them from /admin/theme and every open page picks the change up on its next tuning
 * poll — without a code deploy. The built-in DEFAULT_SITE_THEME below is the fallback that ships in source;
 * anything the admin sets is merged OVER it.
 *
 * applySiteTheme() writes each color as a CSS custom property on :root (the same --site-* variables the
 * stylesheet already consumes) and configures the fixed, non-scrolling background pattern layer. Because we
 * drive the existing variables, every rule that already references them re-skins automatically.
 */

import { tuningKey, onTuning } from "./tuningClient";
import { DEFAULT_PATTERN_PARAMS, patternImageCss, type PatternParams } from "./patternGen";

/** The named background patterns. The simple ones are CSS gradients; `custom` is the procedural SVG pattern
 *  BUILDER (patternGen.ts) driven by `patternParams`. All are pinned with background-attachment: fixed / a
 *  position:fixed layer so they DON'T scroll with the page. */
export type PatternKind =
  | "none"
  | "dots"        // a fine polka grid of soft dots
  | "grid"        // thin engraved graph lines
  | "diagonal"    // 45° hairline stripes (subtle woodgrain feel)
  | "cross"       // small plus/cross-hatch marks
  | "vignette"    // just a soft radial darkening at the edges (no motif)
  | "topo"        // concentric contour rings (organic, map-like)
  | "custom";     // procedural SVG from the pattern builder (gothic / geometric / linework / damask)

export interface SiteTheme {
  /** Core palette — each maps to a --site-* CSS variable. */
  bg: string;          // page ground
  bg2: string;         // gradient deep end
  bgAngle?: number;    // deg — direction of the ground→deep gradient (rotates where each color sits)
  bgAccent: string;    // (retired) formerly a woven-in gradient tint; kept for back-compat
  glow: string;        // (retired) formerly the top light pool; kept for back-compat
  surface: string;     // cards / panels
  surface2: string;    // input wells / secondary fills
  fg: string;          // primary text
  fgDim: string;       // muted text
  border: string;      // soft border
  borderStrong: string;
  brass: string;       // primary accent
  brass2: string;      // primary accent (darker, for gradients)
  slate: string;       // secondary accent
  /** Background pattern (fixed, non-scrolling). */
  pattern: PatternKind;
  patColor: string;    // the pattern's ink color
  patOpacity: number;  // 0..1 overall pattern strength
  patScale: number;    // px — the pattern's tile size / spacing
  patternParams?: PatternParams;   // builder params — used when pattern === "custom"
  /** Editor-only: groups of color-key names that are LINKED (move together by the same HSL delta). Persisted
   *  with the theme so links survive reloads; ignored by applySiteTheme (it only affects editing). */
  linkGroups?: string[][];
}

/** DARK COOL-BROWN + EMERALD + BRASS. "Cool brown" = a desaturated brown with a green-grey undertone (not the
 *  warm red mahogany we had). Emerald is the living accent; brass is the metallic highlight. */
export const DEFAULT_SITE_THEME: SiteTheme = {
  bg: "#231f1a",          // very dark cool brown
  bg2: "#171410",         // deepest — near-black cool umber
  bgAngle: 160,           // gradient direction (deg)
  bgAccent: "#1e2a22",    // retired (back-compat)
  glow: "#33302a",        // retired (back-compat)
  surface: "#2b2621",     // panels — lifted cool brown
  surface2: "#231f1a",    // wells
  fg: "#ece3d4",          // warm-cream ink (kept warm so text feels cozy on the cool ground)
  fgDim: "#b3a894",       // muted taupe
  border: "#3d372f",      // cool-brown border
  borderStrong: "#4f4739",
  brass: "#cba24f",       // brass/gold primary accent
  brass2: "#b08a3c",
  slate: "#4fae86",       // EMERALD as the secondary accent (was slate-blue)
  pattern: "topo",        // subtle organic contour rings by default
  patColor: "#4fae86",    // emerald ink for the pattern
  patOpacity: 0.05,       // very subtle
  patScale: 220,
  patternParams: DEFAULT_PATTERN_PARAMS,
};

/** Merge the admin-set `siteTheme` tuning (if any) OVER the source default. Unknown/missing keys fall back. */
export function resolveSiteTheme(): SiteTheme {
  const o = tuningKey<Partial<SiteTheme>>("siteTheme", {});
  return { ...DEFAULT_SITE_THEME, ...(o && typeof o === "object" ? o : {}) };
}

/** Produce the CSS `background` value for a fixed, non-scrolling pattern layer (or "none"). */
export function patternCss(t: SiteTheme): string {
  const c = t.patColor;
  const s = Math.max(8, t.patScale);
  switch (t.pattern) {
    case "custom":
      return patternImageCss(t.patternParams ?? DEFAULT_PATTERN_PARAMS, s, c);
    case "dots":
      return `radial-gradient(${c} 1.4px, transparent 1.6px)`;
    case "grid":
      return `linear-gradient(${c} 1px, transparent 1px), linear-gradient(90deg, ${c} 1px, transparent 1px)`;
    case "diagonal":
      return `repeating-linear-gradient(45deg, ${c} 0 1px, transparent 1px ${Math.round(s / 12)}px)`;
    case "cross":
      return `linear-gradient(${c} 1px, transparent 1px), linear-gradient(90deg, ${c} 1px, transparent 1px)`;
    case "topo":
      return `radial-gradient(circle at 50% 50%, transparent 0 ${Math.round(s * 0.42)}px, ${c} ${Math.round(s * 0.42)}px ${Math.round(s * 0.42) + 1}px, transparent ${Math.round(s * 0.42) + 1}px)`;
    case "vignette":
      return `radial-gradient(120% 120% at 50% 40%, transparent 55%, ${c} 140%)`;
    case "none":
    default:
      return "none";
  }
}

/** The background-size for the pattern layer, matched to the kind. */
function patternSize(t: SiteTheme): string {
  const s = Math.max(8, t.patScale);
  switch (t.pattern) {
    case "custom": return `${s}px ${s}px`;
    case "dots": return `${Math.round(s / 8)}px ${Math.round(s / 8)}px`;
    case "grid": return `${Math.round(s / 6)}px ${Math.round(s / 6)}px`;
    case "cross": return `${Math.round(s / 5)}px ${Math.round(s / 5)}px`;
    case "topo": return `${s}px ${s}px`;
    case "diagonal": return "auto";
    case "vignette": return "cover";
    default: return "auto";
  }
}

/** Apply a theme to the document: set the --site-* variables + configure the fixed pattern layer. Idempotent. */
export function applySiteTheme(t: SiteTheme): void {
  const r = document.documentElement;
  const set = (k: string, v: string) => r.style.setProperty(k, v);
  set("--site-bg", t.bg);
  set("--site-bg-2", t.bg2);
  set("--site-bg-angle", `${t.bgAngle ?? 160}deg`);
  set("--site-surface", t.surface);
  set("--site-surface-2", t.surface2);
  set("--site-fg", t.fg);
  set("--site-fg-dim", t.fgDim);
  set("--site-border", t.border);
  set("--site-border-strong", t.borderStrong);
  set("--site-brass", t.brass);
  set("--site-brass-2", t.brass2);
  set("--site-brass-soft", hexA(t.brass, 0.16));
  set("--site-slate", t.slate);
  set("--site-slate-2", darken(t.slate, 0.16));   // darker emerald for the primary-button gradient
  set("--site-slate-soft", hexA(t.slate, 0.16));
  // Pattern layer (consumed by the .site::before rule in styles.css).
  set("--pat-image", patternCss(t));
  set("--pat-size", patternSize(t));
  set("--pat-opacity", String(Math.max(0, Math.min(1, t.patOpacity))));
}

/** Parse a #rgb / #rrggbb hex into [r,g,b] (0..255). */
function rgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const n = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  return [parseInt(n.slice(0, 2), 16) || 0, parseInt(n.slice(2, 4), 16) || 0, parseInt(n.slice(4, 6), 16) || 0];
}

/** Convert a #rrggbb hex + alpha into an rgba() string (for the soft accent tints). */
function hexA(hex: string, a: number): string {
  const [r, g, b] = rgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

/** Darken a hex color by `amt` (0..1) toward black — used to derive the button gradient's lower stop. */
function darken(hex: string, amt: number): string {
  const [r, g, b] = rgb(hex);
  const k = 1 - Math.max(0, Math.min(1, amt));
  const to = (v: number) => Math.round(v * k).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

// ── HSL helpers (exported for the theme editor's linked-color adjustments) ──────────────────────────────────
export type HSL = { h: number; s: number; l: number };   // h 0..360, s/l 0..100

/** #rrggbb → HSL. */
export function hexToHsl(hex: string): HSL {
  const [r0, g0, b0] = rgb(hex).map((v) => v / 255) as [number, number, number];
  const max = Math.max(r0, g0, b0), min = Math.min(r0, g0, b0);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r0) h = (g0 - b0) / d + (g0 < b0 ? 6 : 0);
    else if (max === g0) h = (b0 - r0) / d + 2;
    else h = (r0 - g0) / d + 4;
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

/** HSL → #rrggbb. */
export function hslToHex({ h, s, l }: HSL): string {
  h = ((h % 360) + 360) % 360; s = Math.max(0, Math.min(100, s)) / 100; l = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Shift a hex color by an HSL delta (used to propagate a change across linked colors). Clamps s/l to 0..100. */
export function shiftHex(hex: string, d: HSL): string {
  const c = hexToHsl(hex);
  return hslToHex({ h: c.h + d.h, s: c.s + d.s, l: c.l + d.l });
}

/** Boot hook: apply the resolved theme now, and re-apply whenever live tuning changes. Returns an unsubscribe. */
export function startSiteTheme(): () => void {
  applySiteTheme(resolveSiteTheme());
  return onTuning(() => applySiteTheme(resolveSiteTheme()));
}
